import type { UtilizationProductRow } from '../reports/utilizationReport';

export interface WasteActionPeriod { start: string; end: string; label: string }
export interface WasteActionDishUsage {
  dishName: string;
  portions: number;
  perPortionBase: number;
  neededBase: number;
  viaSubrecipes: string[];
}
export interface WasteActionMember {
  key: string;
  name: string;
  supplier: string;
  stdUnit: string;
  orderedBase: number;
  neededBase: number;
  wasteBase: number;
  wasteEur: number | null;
  utilizationPercent: number | null;
  dishes: WasteActionDishUsage[];
}
export interface WasteActionGroupPayload {
  groupId: string;
  groupName: string;
  category: string;
  side: 'food' | 'drinks';
  stdUnit: string | null;
  orderedBase: number | null;
  neededBase: number | null;
  wasteBase: number | null;
  wasteEur: number;
  members: WasteActionMember[];
}
export interface WasteActionItem { id: string; text: string }
export interface WasteFinding extends WasteActionGroupPayload {
  restaurantId: string;
  period: WasteActionPeriod;
  actionItems: WasteActionItem[];
  savedAt: string;
}

/** Snapshot complete alternative groups from the report, never from its filtered view. */
export function buildWasteActionPayload(
  selectedKeys: string[],
  rows: UtilizationProductRow[],
  groupByKey: Record<string, string>,
  groupNames: Record<string, { name: string; category?: string }>,
): WasteActionGroupPayload[] {
  const groupIdFor = (key: string) => groupByKey[key] ?? key;
  const selectedGroups = new Set(selectedKeys.map(groupIdFor));
  const rowsByGroup = new Map<string, UtilizationProductRow[]>();
  for (const row of rows) {
    const groupId = groupIdFor(row.key);
    if (!selectedGroups.has(groupId)) continue;
    const members = rowsByGroup.get(groupId) ?? [];
    members.push(row);
    rowsByGroup.set(groupId, members);
  }

  return Array.from(rowsByGroup, ([groupId, groupRows]) => {
    const sorted = [...groupRows].sort((a, b) => (b.wasteEur ?? 0) - (a.wasteEur ?? 0));
    const top = sorted[0];
    const sameUnit = sorted.every(row => row.stdUnit === top.stdUnit);
    const members: WasteActionMember[] = sorted.map(row => ({
      key: row.key,
      name: row.name,
      supplier: row.supplier,
      stdUnit: row.stdUnit,
      orderedBase: row.orderedBase,
      neededBase: row.neededBase,
      wasteBase: row.wasteBase,
      wasteEur: row.wasteEur,
      utilizationPercent: row.utilizationPercent,
      dishes: row.dishBreakdown.map(dish => ({
        dishName: dish.dishName,
        portions: dish.portions,
        perPortionBase: dish.perPortionBase,
        neededBase: dish.neededBase,
        viaSubrecipes: Array.from(new Set((dish.prepPaths ?? []).flatMap(path => path.hops.map(hop => hop.name)))),
      })),
    }));
    return {
      groupId,
      // Money savers names a group '' when no member was bought in ITS last-60-day window;
      // `||` so an older report period still sends a name (the server rejects '').
      groupName: groupNames[groupId]?.name || top.name,
      category: groupNames[groupId]?.category || top.category || '',
      side: top.side,
      stdUnit: sameUnit ? top.stdUnit : null,
      orderedBase: sameUnit ? members.reduce((sum, member) => sum + member.orderedBase, 0) : null,
      neededBase: sameUnit ? members.reduce((sum, member) => sum + member.neededBase, 0) : null,
      wasteBase: sameUnit ? members.reduce((sum, member) => sum + member.wasteBase, 0) : null,
      wasteEur: members.reduce((sum, member) => sum + (member.wasteEur ?? 0), 0),
      members,
    };
  });
}
